import { DatasetAndItem } from 'helpers/grouping.helper/grouping.helper'
import { getRoute } from 'helpers/route.helper/route.helper'
import { getActiveTags, getTagsByTopLevelTag } from 'hooks/filterHooks/filterHooks'
import { NavigateFunction } from 'react-router'
import getLogger from 'utils/log/log'
import { proxy } from 'valtio'
import { subscribeKey } from 'valtio/utils'
import { GROUPING_QUERYSTRING_PARAM, removeFilterFromUrlWithoutNavigateParam } from './urlFilter.helper'

const log = getLogger('filter')

type FilterStore = {
  globalUrlFilter: string // Global filter state shared across routes
  allTags: Set<string> //["company/bristad", "company/city", "fuel/waste", "fuel/wood"]
  groupingPerRoute: { [key: string]: string } //"followupproduction: "company",
  tagsPerUiconfig: string[][]
  filterComponentPerRoute: { [key: string]: boolean }
  activeTagsForUiConfig: Set<string>
  urlFilters: Record<string, string[]>
  resolvedFilterBlob: string | null
}

export const NO_GROUPING = 'none'

export const initGroupingPerRoute = {
  //TODO: default active grouping tag is set as "group" here as Exergi wants it to be so. When another system than Exergi uses grouping and filter, this should be changed to a more dynamic default approach
  followupproduction: 'group',
  uiconfig: 'group',
  fuelplan: 'group',
  optimize: 'group',
  draftproductionplan: 'group',
  sandbox: 'group',
  // These routes uses filter but not grouping:
  // digitalTwinDeviationSettings: null,
  // digitalTwinBaseSettings: null,
  // events: null,
}

const filterStore = proxy<FilterStore>({
  allTags: new Set<string>(), // This will hold all tags for the current route
  groupingPerRoute: initGroupingPerRoute,
  tagsPerUiconfig: [],
  filterComponentPerRoute: {},
  activeTagsForUiConfig: new Set<string>(),
  urlFilters: {},
  resolvedFilterBlob: null,
  globalUrlFilter: '',
})

type DebugFilterStore = {
  datasetAndItemPerSubTagPerId: {
    [uiConfigId: string]: { [subTag: string]: { [returnId: string]: DatasetAndItem } }
  }
}

export const debugFilterStore = proxy<DebugFilterStore>({
  datasetAndItemPerSubTagPerId: {},
})

// --- Perf helpers: caches, memoization and debounce for expensive recompute ---
let latestUrlFilters: Record<string, string[]> = {}
let latestTagsPerUiconfig: string[][] = []
const activeTagsMemo = new Map<string, Set<string>>()
let debounceHandle: any = null
const DEBOUNCE_MS = 100

function makeMemoKey(tagsByTopLevelTag: Record<string, string[]>, urlFilters: Record<string, string[]>) {
  try {
    return JSON.stringify({ t: tagsByTopLevelTag, u: urlFilters })
  } catch {
    return String(Math.random())
  }
}

function setIfDifferent(newSet: Set<string>) {
  const current = filterStore.activeTagsForUiConfig
  if (current === undefined) {
    filterStore.activeTagsForUiConfig = newSet
    return
  }
  if (current.size !== newSet.size) {
    filterStore.activeTagsForUiConfig = newSet
    return
  }
  for (const v of newSet) {
    if (!current.has(v)) {
      filterStore.activeTagsForUiConfig = newSet
      return
    }
  }
  // identical — do nothing
}

function recomputeActiveTags() {
  const routeTags = [...new Set(latestTagsPerUiconfig.flatMap((filter) => filter))]
  const tagsByTopLevelTag = getTagsByTopLevelTag(routeTags)

  const key = makeMemoKey(tagsByTopLevelTag, latestUrlFilters)
  let activeTagsSet = activeTagsMemo.get(key)
  if (!activeTagsSet) {
    activeTagsSet = getActiveTags(tagsByTopLevelTag, latestUrlFilters)
    activeTagsMemo.set(key, activeTagsSet)
  }

  setIfDifferent(activeTagsSet)
}

function scheduleRecompute() {
  if (debounceHandle) clearTimeout(debounceHandle)
  debounceHandle = setTimeout(() => {
    debounceHandle = null
    try {
      recomputeActiveTags()
    } catch (err) {
      // avoid throwing inside subscriber
      log.error('Error in recomputeActiveTags', err)
    }
  }, DEBOUNCE_MS)
}

function areTagsPerUiconfigEqual(a: string[][], b: string[][]): boolean {
  if (a === b) {
    return true
  }
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}

//whenever the route changes we need to update activeTags for the uiconfigs
subscribeKey(filterStore, 'urlFilters', (urlFilters) => {
  // update cache and schedule recompute to avoid snapshot and heavy synchronous work
  latestUrlFilters = urlFilters || {}
  scheduleRecompute()
})

//whenever a uiconfig component is initialized we need to update active tags
subscribeKey(filterStore, 'tagsPerUiconfig', (tagsPerUiconfig) => {
  // update cache and schedule recompute only if changed; avoid snapshot(filterStore)
  const newTags = tagsPerUiconfig || []
  if (areTagsPerUiconfigEqual(newTags, latestTagsPerUiconfig)) return
  latestTagsPerUiconfig = newTags
  scheduleRecompute()
})

export function setGrouping(tag: string, navigate?: NavigateFunction): void {
  const routesWithGrouping = Object.keys(filterStore.groupingPerRoute)
  // Setting a new grouping applies for all modules with group
  routesWithGrouping.forEach((r) => {
    filterStore.groupingPerRoute[r] = tag
  })

  if (navigate) {
    // Update URL with new grouping
    const currentUrlParams = new URLSearchParams(window.location.search)
    currentUrlParams.set(GROUPING_QUERYSTRING_PARAM, tag)
    const newUrl = `${window.location.pathname}?${currentUrlParams.toString()}${window.location.hash}`
    navigate(newUrl, { replace: true })
  }
}

export function getGrouping() {
  const groupingTag = filterStore.groupingPerRoute[getRoute()]
  if (groupingTag === 'none') {
    return ''
  } else {
    return groupingTag
  }
}

export function resetGrouping(navigate?: NavigateFunction): void {
  // Resets grouping for all modules
  const routesWithGrouping = Object.keys(filterStore.groupingPerRoute)
  routesWithGrouping.forEach((r) => {
    filterStore.groupingPerRoute[r] = NO_GROUPING
  })

  if (navigate) {
    // Deletes group from URL
    const urlParams = new URLSearchParams(window.location.search)
    urlParams.set(GROUPING_QUERYSTRING_PARAM, NO_GROUPING)
    const queryString = urlParams.toString()
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}${window.location.hash}`
      : `${window.location.pathname}${window.location.hash}`
    navigate(newUrl, { replace: true })
  }
}

export function syncSystemTagsWithStore(filterTagsForSystem?: string[]) {
  const allTagsInStore = filterStore.allTags
  if (
    allTagsInStore.size > 0 &&
    (allTagsInStore.size !== filterTagsForSystem?.length || filterTagsForSystem.some((tag) => !allTagsInStore.has(tag)))
  ) {
    // There are tags in store, but they don't match the tags we get from backend for the current system
    // -> Reset the store and URL
    resetFilterTags()
  }

  if (filterTagsForSystem !== undefined) {
    setAllTags(filterTagsForSystem)
  }
}

function resetFilterTags(): void {
  filterStore.globalUrlFilter = ''
  filterStore.allTags = new Set<string>()
  filterStore.groupingPerRoute = initGroupingPerRoute
  filterStore.tagsPerUiconfig = []
  filterStore.filterComponentPerRoute = {}
  removeFilterFromUrlWithoutNavigateParam()
}

function setAllTags(allTags: string[]): void {
  filterStore.allTags = new Set(allTags)
}

export function getTopLevelTagsFromTags(activeTags: string[]): Set<string> {
  const tags = activeTags
  const topLevelTags = new Set<string>()

  if (tags) {
    tags.forEach((tag) => {
      const topLevelTag = tag.split('/')[0]
      topLevelTags.add(topLevelTag)
    })
  }
  return topLevelTags
}

export default filterStore
